import { styled, View } from 'dripsy';
import React from 'react';
import { ModalComponentProp } from 'react-native-modalfy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { Post } from '@models/Posts/PostModel';
import { ModalStackParamsType } from '../../modals/types';
import { ModalView } from './components/ModalView';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { useAsyncInit } from '@hooks';
import { BaseText } from '../common/BaseText';
import { IconTouchable } from '../common/IconTouchable';
import { User } from '@models/Viewer/UserModel';
import { LikesUserList } from '../business/LikesUserList';

const ModalViewContainer = styled(ModalView)(() => ({
  height: 480,
  width: 450,
  px: '$6',
}));

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const HeaderContainer = styled(RowContainer)({
  justifyContent: 'space-between',
  pt: '$6',
});

const Title = styled(BaseText)({
  fontWeight: 'semi',
  color: '$heading800',
  variant: 'text.xlarge',
});

const IconLeft = styled(IconTouchable)({
  p: '$1',
});

const contentContainerStyle = {
  flexGrow: 1,
};

export interface ILikesModalParams {
  item: Post;
  onNavigateToUser(item: User): void;
  imageIndex?: number;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'LikesModal'
>;

export const LikesModal = observer(({ modal }: ModalPropsType) => {
  const { likes: list } = modal.getParam('item');
  const onNavigateToUserParam = modal.getParam('onNavigateToUser');
  const { t } = useTranslation();

  useAsyncInit(() => fetch());

  async function fetch() {
    try {
      await list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function fetchMore() {
    try {
      await list.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function onNavigateToUser(item: User) {
    modal.closeModal();
    onNavigateToUserParam(item);
  }

  return (
    <ModalViewContainer>
      <HeaderContainer>
        <Title>{t('likes.title')}</Title>
        <IconLeft name="close-light" onPress={modal.closeModal} />
      </HeaderContainer>
      <LikesUserList
        list={list}
        refetch={fetch}
        fetchMore={fetchMore}
        onPress={onNavigateToUser}
        contentContainerStyle={contentContainerStyle}
      />
    </ModalViewContainer>
  );
});
